import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { AutoFillInputTemplate as template } from './auto-fill-input.template';
import { AutoFillInputStyles as styles } from './auto-fill-input.styles';
import { TextField } from '@genesislcap/foundation-zero';
import { clearErrorUtil, displayErrorUtil } from '../../../utils';
import { IProjectModalService } from '../../../services';

const name = 'auto-fill-input';

@customElement({
  name,
  template,
  styles,
})
export class AutoFillInput extends FASTElement {
  @IProjectModalService projectModalService: IProjectModalService;
  @observable autofillElement: TextField;
  @observable wrapper: HTMLDivElement;
  @observable autofillElementValue: string = '';
  @observable autofillValueID: string = '';
  @observable listContainer: HTMLElement;
  @observable disabled: boolean = true;
  @observable timer: NodeJS.Timeout;
  @observable filteredList = [];
  @observable showList: boolean = false;
  @attr header: string = '';
  @attr required: boolean = false;
  @attr dataArray = [];

  connectedCallback(): void {
    super.connectedCallback();
    this.autofillElement.addEventListener('keyup', () => {
      if (!this.autofillElementValue) {
        this.showList = false;
      }
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.filterData();
      }, 300);
    });

    this.autofillElement.addEventListener('keydown', (event) => {
      if (this.showList && (event.key == 'ArrowDown' || event.key == 'ArrowUp')) {
        const selectedElement = this.listContainer.querySelector('.selected');
        if (selectedElement) {
          const newIndex = this.calculateNewIndex(selectedElement, event.key);
          selectedElement.classList.remove('selected');
          this.listContainer.children[newIndex].classList.add('selected');
          this.listContainer.scrollTop = (
            this.listContainer.children[newIndex] as HTMLElement
          ).offsetTop;
        } else {
          this.listContainer.children[0].classList.add('selected');
        }
      } else if (this.showList && event.key == 'Enter') {
        const selectedElement = this.listContainer.querySelector('.selected');
        if (selectedElement) {
          const selElem = this.filteredList.find((item) => item.NAME == selectedElement.innerHTML);
          this.selectValue(selElem);
        }
      } else {
        clearTimeout(this.timer);
      }
    });

    this.autofillElement.addEventListener('focusin', () => {
      this.filterData();
    });

    this.autofillElement.addEventListener('blur', () => {
      this.validateInput();
    });

    this.wrapper.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!this.wrapper.contains(document.activeElement)) {
          if (!this.filteredList.find((item) => item.NAME === this.autofillElementValue)) {
            this.autofillValueID = '';
            this.$emit('isAutofillValueIdValid', this.autofillValueID !== '');
            // Since there is no matching id for the value, the text in the field is invalid
            displayErrorUtil(this.autofillElement, 'Please select a value from the available list');
            this.removeFromValidList(this.autofillElement);
          }
          this.showList = false;
        }
      }, 300);
    });
  }

  private calculateNewIndex(element: Element, key: 'ArrowDown' | 'ArrowUp'): number {
    const childIndex = Array.from(element.parentNode.children).indexOf(element);
    const numberOfSiblings = this.listContainer.children.length;
    if (key == 'ArrowDown') {
      return childIndex + 1 >= numberOfSiblings ? 0 : childIndex + 1;
    } else if (key == 'ArrowUp') {
      return childIndex <= 0 ? numberOfSiblings - 1 : childIndex - 1;
    }
  }

  private validateInput() {
    if (!this.autofillElementValue) {
      displayErrorUtil(this.autofillElement);
      this.autofillValueID = '';
      this.$emit('isAutofillValueIdValid', this.autofillValueID !== '');
      this.removeFromValidList(this.autofillElement);
    } else {
      clearErrorUtil(this.autofillElement);
      this.appendToValidList(this.autofillElement);
    }
  }

  private appendToValidList(field: TextField) {
    this.projectModalService.addToTotalCompletedFields(field);
  }

  private removeFromValidList(field: TextField) {
    this.projectModalService.removeFromTotalCompletedFields(field);
  }

  public filterData() {
    this.filteredList = [];
    if (this.autofillElementValue) {
      this.filteredList = this.dataArray.filter((item) =>
        item.NAME.toLowerCase().includes(this.autofillElementValue.toLowerCase()),
      );
      this.showList = true;
      setTimeout(() => {
        this.calculatePosition();
      }, 100);
    }
  }

  public selectValue(value) {
    this.autofillElementValue = value.NAME;
    this.autofillValueID = value.RESURETY_LOCATION_ID;
    this.$emit('isAutofillValueIdValid', this.autofillValueID !== '');
    this.showList = false;
  }

  public setResuretyLocationID(id: string) {
    const resRecord = this.dataArray.find((item) => item.RESURETY_LOCATION_ID == id);

    this.autofillValueID = id;
    this.$emit('isAutofillValueIdValid', this.autofillValueID !== '');
    this.autofillElementValue = resRecord ? resRecord.NAME : '';
  }

  public calculatePosition() {
    let viewportOffset = this.autofillElement.getBoundingClientRect();
    const top = viewportOffset.top - 180;
    const bottom = window.innerHeight - viewportOffset.bottom - 70;

    if (top > bottom) {
      this.listContainer.style.bottom = '48px';
      this.listContainer.style.maxHeight = top - 20 + 'px';
    } else {
      this.listContainer.style.top = '76px';
      this.listContainer.style.maxHeight = bottom - 20 + 'px';
    }
  }

  public resetFilter() {
    this.filteredList = this.dataArray.filter((item) =>
      item.NAME.toLowerCase().includes(this.autofillElementValue.toLowerCase()),
    );
    this.showList = false;
  }
}
